import { PDFDocument } from 'pdf-lib'

export class Pdf2PdfApp {
  constructor() {
    this.container = null;
    this.selectedFiles = [];
    this.settings = this.getDefaultSettings();
  }

  getDefaultSettings() {
    return {
      // Main settings (visible by default)
      mergeMode: 'all', // 'all', 'ranges', 'custom'
      preserveBookmarks: true,
      preserveMetadata: true,
      outputFilename: 'merged-document.pdf',
      
      // Advanced settings (hidden by default)
      quality: 'medium',
      compression: true,
      createBookmarks: false,
      bookmarkStyle: 'filename', // 'filename', 'custom', 'none'
      
      // Metadata for merged PDF
      title: 'Merged PDF Document',
      author: '',
      subject: 'Merged PDFs',
      keywords: ['merged', 'pdf'],
      creator: 'PDF Merger Tool',
      producer: 'PDF Merger Tool'
    };
  }

  init(container) {
    this.container = container;
    this.render();
    this.setupEventListeners();
  }

  render() {
    this.container.innerHTML = `
      <div class="container">
        <div class="pdf2pdf-app">
          <!-- File Upload Section -->
          <div class="upload-section">
            <div class="upload-area" id="upload-area">
              <div class="upload-icon">📄</div>
              <h3>Select PDF Files to Merge</h3>
              <p>Choose multiple PDF files to combine into one document</p>
              <button class="upload-button" id="file-select-btn">
                <span>📎 Choose PDF Files</span>
              </button>
              <input type="file" id="file-input" accept=".pdf,application/pdf" multiple style="display: none;">
              <div class="upload-hint">Or drag and drop PDF files here</div>
            </div>
          </div>

          <!-- File Order Section -->
          <div class="file-order-section" id="file-order-section" style="display: none;">
            <div class="section-header">
              <h3>📋 Files to Merge</h3>
              <p>Drag files to reorder • Click ⚙️ for page range settings</p>
            </div>
            
            <div class="files-list-container">
              <div class="files-list" id="files-list"></div>
              <div class="list-actions">
                <button class="action-button" id="clear-files">🗑️ Clear All</button>
                <button class="action-button" id="add-more-files">➕ Add More Files</button>
              </div>
            </div>
          </div>

          <!-- Settings Section -->
          <div class="settings-section" id="settings-section" style="display: none;">
            <h3>Settings</h3>
            
            <!-- Main Settings -->
            <div class="settings-group main-settings">
              <div class="setting-row">
                <label for="output-filename">📄 Output Filename:</label>
                <input type="text" id="output-filename" class="setting-input" value="merged-document.pdf">
              </div>

              <div class="setting-row">
                <label for="preserve-bookmarks">🔖 Preserve Bookmarks:</label>
                <div class="checkbox-group">
                  <input type="checkbox" id="preserve-bookmarks" class="setting-checkbox" checked>
                  <span class="checkbox-label">Keep existing PDF bookmarks</span>
                </div>
              </div>

              <div class="setting-row">
                <label for="preserve-metadata">📋 Preserve Metadata:</label>
                <div class="checkbox-group">
                  <input type="checkbox" id="preserve-metadata" class="setting-checkbox" checked>
                  <span class="checkbox-label">Keep original PDF metadata when possible</span>
                </div>
              </div>

              <div class="setting-row">
                <label for="create-bookmarks">📑 Create File Bookmarks:</label>
                <div class="checkbox-group">
                  <input type="checkbox" id="create-bookmarks" class="setting-checkbox">
                  <span class="checkbox-label">Add bookmarks for each merged file</span>
                </div>
              </div>
            </div>

            <!-- Advanced Settings Toggle -->
            <button class="advanced-toggle" id="advanced-toggle">
              <span>⚙️ Advanced Settings</span>
              <span class="toggle-icon">▼</span>
            </button>

            <!-- Advanced Settings (Hidden by default) -->
            <div class="settings-group advanced-settings" id="advanced-settings" style="display: none;">
              <div class="setting-row">
                <label for="quality">✨ Processing Quality:</label>
                <select id="quality" class="setting-input">
                  <option value="high">High (preserve all details)</option>
                  <option value="medium" selected>Medium (balanced)</option>
                  <option value="low">Low (faster processing)</option>
                </select>
              </div>

              <div class="setting-row">
                <label for="compression">🗜️ PDF Compression:</label>
                <div class="checkbox-group">
                  <input type="checkbox" id="compression" class="setting-checkbox" checked>
                  <span class="checkbox-label">Compress output PDF (smaller file size)</span>
                </div>
              </div>

              <div class="setting-row">
                <label for="bookmark-style">🏷️ Bookmark Style:</label>
                <select id="bookmark-style" class="setting-input">
                  <option value="filename">Use filename as bookmark</option>
                  <option value="title">Use PDF title as bookmark</option>
                  <option value="custom">Custom bookmark names</option>
                </select>
              </div>

              <!-- Metadata -->
              <div class="metadata-group">
                <h4>📄 Output PDF Metadata</h4>
                <div class="metadata-inputs">
                  <input type="text" id="pdf-title" class="metadata-input" placeholder="Document Title" value="Merged PDF Document">
                  <input type="text" id="pdf-author" class="metadata-input" placeholder="Author">
                  <input type="text" id="pdf-subject" class="metadata-input" placeholder="Subject" value="Merged PDFs">
                </div>
              </div>
            </div>
          </div>

          <!-- Merge Section -->
          <div class="merge-section" id="merge-section" style="display: none;">
            <button class="merge-button" id="merge-button" disabled>
              <span class="merge-icon">🔗</span>
              <span class="merge-text">Merge PDFs</span>
            </button>
            
            <div class="progress-section" id="progress-section" style="display: none;">
              <div class="progress-bar">
                <div class="progress-fill" id="progress-fill"></div>
              </div>
              <div class="progress-text" id="progress-text">Merging...</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  setupEventListeners() {
    const fileInput = document.getElementById('file-input');
    const fileSelectBtn = document.getElementById('file-select-btn');
    const uploadArea = document.getElementById('upload-area');
    const clearFilesBtn = document.getElementById('clear-files');
    const addMoreFilesBtn = document.getElementById('add-more-files');
    const mergeBtn = document.getElementById('merge-button');
    const advancedToggle = document.getElementById('advanced-toggle');

    // File selection
    fileSelectBtn.addEventListener('click', () => fileInput.click());
    addMoreFilesBtn.addEventListener('click', () => fileInput.click());
    fileInput.addEventListener('change', (e) => this.handleFileSelection(e.target.files));

    // Drag and drop
    uploadArea.addEventListener('dragover', this.handleDragOver.bind(this));
    uploadArea.addEventListener('drop', this.handleFileDrop.bind(this));
    uploadArea.addEventListener('dragleave', this.handleDragLeave.bind(this));

    // Clear files
    clearFilesBtn.addEventListener('click', () => this.clearFiles());

    // Merge button
    mergeBtn.addEventListener('click', () => this.mergePDFs());

    // Advanced settings toggle
    advancedToggle.addEventListener('click', () => this.toggleAdvancedSettings());

    // Settings change listeners
    this.setupSettingsListeners();
  }

  setupSettingsListeners() {
    // Main settings
    document.getElementById('output-filename').addEventListener('change', (e) => {
      this.settings.outputFilename = e.target.value;
    });

    document.getElementById('preserve-bookmarks').addEventListener('change', (e) => {
      this.settings.preserveBookmarks = e.target.checked;
    });

    document.getElementById('preserve-metadata').addEventListener('change', (e) => {
      this.settings.preserveMetadata = e.target.checked;
    });

    document.getElementById('create-bookmarks').addEventListener('change', (e) => {
      this.settings.createBookmarks = e.target.checked;
    });

    // Advanced settings
    document.getElementById('quality').addEventListener('change', (e) => {
      this.settings.quality = e.target.value;
    });

    document.getElementById('compression').addEventListener('change', (e) => {
      this.settings.compression = e.target.checked;
    });

    document.getElementById('bookmark-style').addEventListener('change', (e) => {
      this.settings.bookmarkStyle = e.target.value;
    });

    // Metadata
    document.getElementById('pdf-title').addEventListener('change', (e) => {
      this.settings.title = e.target.value;
    });

    document.getElementById('pdf-author').addEventListener('change', (e) => {
      this.settings.author = e.target.value;
    });

    document.getElementById('pdf-subject').addEventListener('change', (e) => {
      this.settings.subject = e.target.value;
    });
  }

  handleFileSelection(files) {
    const validFiles = Array.from(files).filter(file => {
      return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    });

    if (validFiles.length === 0) {
      this.showError('Please select valid PDF files');
      return;
    }

    // Add new files to existing ones (for "Add More Files" functionality)
    validFiles.forEach(file => {
      // Avoid duplicates
      if (!this.selectedFiles.find(existing => existing.name === file.name && existing.size === file.size)) {
        this.selectedFiles.push({
          file: file,
          pageRange: 'all', // 'all', 'range', 'custom'
          customRange: '',
          customBookmark: file.name.replace('.pdf', ''),
          id: this.generateFileId()
        });
      }
    });

    this.updateFilesList();
    this.updateUI();
  }

  handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add('drag-over');
  }

  handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('drag-over');
  }

  handleFileDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('drag-over');
    
    const files = e.dataTransfer.files;
    this.handleFileSelection(files);
  }

  updateFilesList() {
    const filesList = document.getElementById('files-list');
    
    if (this.selectedFiles.length === 0) {
      return;
    }

    filesList.innerHTML = '';

    this.selectedFiles.forEach((fileData, index) => {
      const fileItem = document.createElement('div');
      fileItem.className = 'file-item';
      fileItem.draggable = true;
      fileItem.dataset.index = index;
      
      fileItem.innerHTML = `
        <div class="file-drag-handle">⋮⋮</div>
        <div class="file-info">
          <div class="file-name">${fileData.file.name}</div>
          <div class="file-details">
            <span class="file-size">${this.formatFileSize(fileData.file.size)}</span>
            <span class="file-pages" id="file-pages-${index}">Loading...</span>
          </div>
        </div>
        <div class="file-settings">
          <div class="page-range-selector">
            <select class="page-range-select" data-index="${index}">
              <option value="all">All pages</option>
              <option value="range">Page range</option>
            </select>
            <input type="text" class="page-range-input" data-index="${index}" 
                   placeholder="e.g., 1-5, 10, 15-20" style="display: none;">
          </div>
          <button class="remove-file-btn" data-index="${index}" title="Remove file">×</button>
        </div>
      `;

      // Load PDF to get page count
      this.loadPDFInfo(fileData.file, index);
      
      // Add drag and drop event listeners
      this.setupFileDragAndDrop(fileItem, index);
      
      filesList.appendChild(fileItem);
    });

    // Setup event listeners for the new elements
    this.setupFileItemListeners();
  }

  setupFileDragAndDrop(fileItem, index) {
    fileItem.addEventListener('dragstart', (e) => {
      e.dataTransfer.setData('text/plain', index);
      fileItem.classList.add('dragging');
    });

    fileItem.addEventListener('dragend', () => {
      fileItem.classList.remove('dragging');
    });

    fileItem.addEventListener('dragover', (e) => {
      e.preventDefault();
      const draggingItem = document.querySelector('.dragging');
      if (draggingItem !== fileItem) {
        fileItem.classList.add('drag-over');
      }
    });

    fileItem.addEventListener('dragleave', () => {
      fileItem.classList.remove('drag-over');
    });

    fileItem.addEventListener('drop', (e) => {
      e.preventDefault();
      fileItem.classList.remove('drag-over');
      
      const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
      const dropIndex = parseInt(fileItem.dataset.index);
      
      if (dragIndex !== dropIndex) {
        this.reorderFiles(dragIndex, dropIndex);
      }
    });
  }

  setupFileItemListeners() {
    // Page range selectors
    document.querySelectorAll('.page-range-select').forEach(select => {
      select.addEventListener('change', (e) => {
        const index = parseInt(e.target.dataset.index);
        const rangeInput = document.querySelector(`.page-range-input[data-index="${index}"]`);
        
        this.selectedFiles[index].pageRange = e.target.value;
        
        if (e.target.value === 'range') {
          rangeInput.style.display = 'block';
        } else {
          rangeInput.style.display = 'none';
          this.selectedFiles[index].customRange = '';
        }
      });
    });

    // Page range inputs
    document.querySelectorAll('.page-range-input').forEach(input => {
      input.addEventListener('change', (e) => {
        const index = parseInt(e.target.dataset.index);
        this.selectedFiles[index].customRange = e.target.value;
      });
    });

    // Remove file buttons
    document.querySelectorAll('.remove-file-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const index = parseInt(e.target.dataset.index);
        this.removeFile(index);
      });
    });

  }

  async loadPDFInfo(file, index) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const pageCount = pdfDoc.getPageCount();
      
      const pagesElement = document.getElementById(`file-pages-${index}`);
      if (pagesElement) {
        pagesElement.textContent = `${pageCount} pages`;
      }
    } catch (error) {
      const pagesElement = document.getElementById(`file-pages-${index}`);
      if (pagesElement) {
        pagesElement.textContent = 'Error reading PDF';
      }
    }
  }

  reorderFiles(fromIndex, toIndex) {
    const [movedFile] = this.selectedFiles.splice(fromIndex, 1);
    this.selectedFiles.splice(toIndex, 0, movedFile);
    this.updateFilesList();
  }

  removeFile(index) {
    this.selectedFiles.splice(index, 1);
    this.updateFilesList();
    this.updateUI();
  }

  clearFiles() {
    this.selectedFiles = [];
    this.updateFilesList();
    this.updateUI();
  }

  updateUI() {
    const hasFiles = this.selectedFiles.length > 0;
    
    // Show/hide sections based on file selection
    document.getElementById('file-order-section').style.display = hasFiles ? 'block' : 'none';
    document.getElementById('settings-section').style.display = hasFiles ? 'block' : 'none';
    document.getElementById('merge-section').style.display = hasFiles ? 'block' : 'none';
    
    // Enable/disable merge button
    const mergeBtn = document.getElementById('merge-button');
    mergeBtn.disabled = this.selectedFiles.length < 2;
    
    if (this.selectedFiles.length === 1) {
      mergeBtn.innerHTML = '<span class="merge-icon">⚠️</span><span class="merge-text">Need at least 2 PDFs to merge</span>';
    } else if (this.selectedFiles.length > 1) {
      mergeBtn.innerHTML = `<span class="merge-icon">🔗</span><span class="merge-text">Merge ${this.selectedFiles.length} PDFs</span>`;
    }
  }

  toggleAdvancedSettings() {
    const advancedSettings = document.getElementById('advanced-settings');
    const toggleIcon = document.querySelector('.toggle-icon');
    
    if (advancedSettings.style.display === 'none') {
      advancedSettings.style.display = 'block';
      toggleIcon.textContent = '▲';
    } else {
      advancedSettings.style.display = 'none';
      toggleIcon.textContent = '▼';
    }
  }

  async mergePDFs() {
    if (this.selectedFiles.length < 2) {
      this.showError('Please select at least 2 PDF files to merge');
      return;
    }

    try {
      this.showProgress('Initializing merge...');
      
      const mergedPdf = await PDFDocument.create();
      
      // Set metadata for merged PDF
      mergedPdf.setTitle(this.settings.title);
      mergedPdf.setAuthor(this.settings.author);
      mergedPdf.setSubject(this.settings.subject);
      mergedPdf.setKeywords(this.settings.keywords);
      mergedPdf.setCreator(this.settings.creator);
      mergedPdf.setProducer(this.settings.producer);
      mergedPdf.setCreationDate(new Date());

      let totalProgress = 0;
      const progressPerFile = 1 / this.selectedFiles.length;

      for (let i = 0; i < this.selectedFiles.length; i++) {
        const fileData = this.selectedFiles[i];
        
        this.updateProgress(
          `Processing ${fileData.file.name}...`,
          totalProgress
        );

        await this.mergePDFFile(mergedPdf, fileData, i);
        totalProgress += progressPerFile * 0.8; // 80% for processing files
      }

      this.updateProgress('Finalizing merged PDF...', 0.9);

      // Save the merged PDF
      const mergedPdfBytes = await mergedPdf.save();
      
      this.updateProgress('Download ready!', 1.0);
      
      // Download the file
      this.downloadFile(mergedPdfBytes, this.settings.outputFilename);
      
      this.hideProgress();
      this.showSuccess(`Successfully merged ${this.selectedFiles.length} PDF files!`);

    } catch (error) {
      console.error('Error merging PDFs:', error);
      this.hideProgress();
      this.showError(`Merge failed: ${error.message}`);
    }
  }

  async mergePDFFile(mergedPdf, fileData, fileIndex) {
    const arrayBuffer = await fileData.file.arrayBuffer();
    const sourcePdf = await PDFDocument.load(arrayBuffer);
    
    // Determine which pages to copy
    let pageIndices;
    if (fileData.pageRange === 'all') {
      pageIndices = Array.from({ length: sourcePdf.getPageCount() }, (_, i) => i);
    } else if (fileData.pageRange === 'range' && fileData.customRange) {
      pageIndices = this.parsePageRange(fileData.customRange, sourcePdf.getPageCount());
    } else {
      pageIndices = Array.from({ length: sourcePdf.getPageCount() }, (_, i) => i);
    }

    // Copy pages
    const copiedPages = await mergedPdf.copyPages(sourcePdf, pageIndices);
    
    // Add bookmark for this file if requested
    if (this.settings.createBookmarks && copiedPages.length > 0) {
      const bookmarkName = this.getBookmarkName(fileData, sourcePdf);
      // Note: pdf-lib doesn't directly support bookmarks, but we can add them as metadata
      // This would require additional library or manual PDF structure manipulation
    }

    // Add all copied pages to the merged document
    copiedPages.forEach(page => {
      mergedPdf.addPage(page);
    });

    // Preserve metadata if requested
    if (this.settings.preserveMetadata && fileIndex === 0) {
      // Use metadata from first PDF if preserveMetadata is true
      try {
        const title = sourcePdf.getTitle();
        const author = sourcePdf.getAuthor();
        const subject = sourcePdf.getSubject();
        
        if (title && !this.settings.title) mergedPdf.setTitle(title);
        if (author && !this.settings.author) mergedPdf.setAuthor(author);
        if (subject && !this.settings.subject) mergedPdf.setSubject(subject);
      } catch (e) {
        // Ignore metadata errors
      }
    }
  }

  parsePageRange(rangeString, totalPages) {
    const indices = [];
    const ranges = rangeString.split(',').map(s => s.trim());
    
    for (const range of ranges) {
      if (range.includes('-')) {
        const [start, end] = range.split('-').map(s => parseInt(s.trim()));
        if (start && end && start <= totalPages && end <= totalPages && start <= end) {
          for (let i = start - 1; i < end; i++) { // Convert to 0-based index
            if (i >= 0 && i < totalPages && !indices.includes(i)) {
              indices.push(i);
            }
          }
        }
      } else {
        const pageNum = parseInt(range);
        if (pageNum && pageNum <= totalPages) {
          const index = pageNum - 1; // Convert to 0-based index
          if (index >= 0 && !indices.includes(index)) {
            indices.push(index);
          }
        }
      }
    }
    
    return indices.sort((a, b) => a - b);
  }

  getBookmarkName(fileData, sourcePdf) {
    switch (this.settings.bookmarkStyle) {
      case 'title':
        try {
          const title = sourcePdf.getTitle();
          return title || fileData.customBookmark;
        } catch {
          return fileData.customBookmark;
        }
      case 'custom':
        return fileData.customBookmark;
      case 'filename':
      default:
        return fileData.file.name.replace('.pdf', '');
    }
  }

  generateFileId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  downloadFile(data, filename) {
    const blob = new Blob([data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  showProgress(message) {
    const progressSection = document.getElementById('progress-section');
    const mergeButton = document.getElementById('merge-button');
    
    progressSection.style.display = 'block';
    mergeButton.disabled = true;
    
    this.updateProgress(message, 0);
  }

  updateProgress(message, progress) {
    const progressText = document.getElementById('progress-text');
    const progressFill = document.getElementById('progress-fill');
    
    progressText.textContent = message;
    progressFill.style.width = `${Math.round(progress * 100)}%`;
  }

  hideProgress() {
    const progressSection = document.getElementById('progress-section');
    const mergeButton = document.getElementById('merge-button');
    
    setTimeout(() => {
      progressSection.style.display = 'none';
      mergeButton.disabled = this.selectedFiles.length < 2;
    }, 2000);
  }

  showSuccess(message) {
    this.showNotification(message, 'success');
  }

  showError(message) {
    this.showNotification(message, 'error');
  }

  showNotification(message, type) {
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.textContent = message;
    
    this.container.appendChild(notification);
    
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 5000);
  }

  destroy() {
    this.selectedFiles = [];
    if (this.container) {
      this.container.innerHTML = '';
    }
  }
}